import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import { ApiClientError, type Dashboard } from "@/lib/types";
import { AuthStatePanels } from "@/components/AuthState";
import { ButtonLink } from "@/components/ButtonLink";
import { FolderKanban, LayoutDashboard, ListTodo } from "@/components/icons";
import { LoadList } from "@/components/LoadList";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

const recentStates = ["queued", "running", "cancel_requested", "succeeded", "failed", "canceled"] as const;

export function DashboardPage() {
  const { m, locale } = useLocale();
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .dashboard()
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [m.states.error]);

  if (loading) return <LoadList title={m.common.loading} />;

  const mix = recentStates
    .map((state) => ({
      state,
      count: data?.recent_tasks.filter((task) => task.state === state).length ?? 0,
    }))
    .filter((item) => item.count > 0);
  const mixMax = Math.max(1, ...mix.map((item) => item.count));

  return (
    <AuthStatePanels loading={false} error={error} loadingTitle={m.common.loading}>
      {!data ? (
        <StatePanel kind="empty" title={m.states.empty} />
      ) : (
        <div>
          <h1 className={shell.pageTitle}>{m.dashboard.title}</h1>
          <p className={shell.pageLead}>{m.dashboard.lead}</p>

          <div className={shell.statGrid} aria-label={m.dashboard.title}>
            <Link className={shell.stat} to="/app/projects">
              <FolderKanban size={18} aria-hidden />
              <strong>{data.counts.projects}</strong>
              <span>{m.dashboard.projects}</span>
            </Link>
            <Link className={shell.stat} to="/app/tasks">
              <ListTodo size={18} aria-hidden />
              <strong>{data.counts.tasks}</strong>
              <span>{m.dashboard.tasks}</span>
            </Link>
            <Link className={shell.stat} to="/app/environment">
              <LayoutDashboard size={18} aria-hidden />
              <strong>{data.counts.active_leases}</strong>
              <span>{m.dashboard.activeLeases}</span>
            </Link>
          </div>

          {mix.length > 0 ? (
            <section aria-label={m.dashboard.recentStates}>
              <h2 className={`display ${ui.sectionHead}`}>
                <ListTodo size={18} aria-hidden />
                {m.dashboard.recentStates}
              </h2>
              <p className={ui.meta}>{m.dashboard.recentStatesNote}</p>
              <div className={shell.mix}>
                {mix.map((item) => (
                  <div key={item.state} className={shell.mixRow}>
                    <span>{lifecycleLabel(m, item.state)}</span>
                    <span className={shell.mixTrack}>
                      <span
                        className={shell.mixFill}
                        data-state={item.state}
                        style={{ width: `${(item.count / mixMax) * 100}%` }}
                      />
                    </span>
                    <span className={shell.mixCount}>{item.count}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {data.counts.projects === 0 ? (
            <div style={{ marginBottom: "1.5rem" }}>
              <StatePanel kind="empty" title={m.dashboard.noProjects} message={m.dashboard.noProjectsBody} />
              <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
                <ButtonLink to="/app/projects" variant="primary">
                  {m.dashboard.openProjects}
                </ButtonLink>
              </div>
            </div>
          ) : null}

          <h2 className="display" style={{ fontSize: "1.1rem", fontWeight: 600 }}>
            {m.dashboard.recentTasks}
          </h2>
          {data.recent_tasks.length === 0 ? (
            <div>
              <StatePanel kind="empty" title={m.dashboard.noTasks} message={m.dashboard.noTasksBody} />
              <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
                <ButtonLink to="/app/projects" variant="secondary">
                  {m.dashboard.openProjects}
                </ButtonLink>
              </div>
            </div>
          ) : (
            <div>
              {data.recent_tasks.map((task) => (
                <Link key={task.id} to={`/app/tasks/${task.id}`} className={ui.row}>
                  <StatusRail state={task.state} pulse={task.state === "running"} />
                  <span className={ui.rowMain}>
                    <span className={ui.rowIcon}>
                      <ListTodo size={18} aria-hidden />
                    </span>
                    <span>
                    <strong>{task.title}</strong>
                    <div className={ui.meta}>
                      {lifecycleLabel(m, task.state)} · {lifecycleLabel(m, task.kind)} ·{" "}
                      {formatWhen(task.updated_at, locale)}
                    </div>
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </AuthStatePanels>
  );
}
